import { Injectable } from '@angular/core';
import { AuthState, ProductCategory, ProductType } from '../models/auth-state.model';

const AUTH_STATE_KEY = 'auth_state';
const LEGACY_TOKEN_KEY = 'token';
const SESSION_AUTH_KEY = 'auth_state_session';

function parseJwtExp(token: string): number | undefined {
  try {
    const [, payload] = token.split('.');
    if (!payload) {
      return undefined;
    }
    let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }
    const json = JSON.parse(atob(base64));
    const exp = json?.exp;
    return typeof exp === 'number' ? exp : undefined;
  } catch {
    return undefined;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthStateService {
  // undefined = not yet loaded; null = loaded but absent
  private cached: AuthState | null | undefined = undefined;

  load(): AuthState | null {
    if (this.cached !== undefined) {
      return this.cached;
    }
    const raw = localStorage.getItem(AUTH_STATE_KEY) ?? sessionStorage.getItem(SESSION_AUTH_KEY);
    if (!raw) {
      this.cached = null;
      return null;
    }
    try {
      this.cached = JSON.parse(raw) as AuthState;
      return this.cached;
    } catch {
      this.cached = null;
      return null;
    }
  }

  save(state: AuthState): void {
    this.cached = state;
    localStorage.setItem(AUTH_STATE_KEY, JSON.stringify(state));
    // Do not write an empty token to the legacy key (SSO sessions have no JWT).
    if (state.token) {
      localStorage.setItem(LEGACY_TOKEN_KEY, state.token);
    }
  }

  saveSession(state: AuthState): void {
    this.cached = state;
    sessionStorage.setItem(SESSION_AUTH_KEY, JSON.stringify(state));
  }

  clear(): void {
    this.cached = null;
    localStorage.removeItem(AUTH_STATE_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem('expires_at');
    localStorage.removeItem('userDetails');
    sessionStorage.removeItem(SESSION_AUTH_KEY);
    sessionStorage.removeItem('ssoSession');
  }

  get token(): string | null {
    return this.load()?.token ?? localStorage.getItem(LEGACY_TOKEN_KEY);
  }

  get roles(): string[] {
    return this.load()?.roles ?? [];
  }

  get productType(): ProductType | null {
    return this.load()?.productType ?? null;
  }

  get brandId(): string | null {
    return this.load()?.brandId ?? null;
  }

  get categories(): ProductCategory[] {
    return this.load()?.categories ?? [];
  }

  get defaultCategoryId(): string | null {
    return this.load()?.defaultCategoryId ?? null;
  }

  get activeCategoryId(): string | null {
    return this.load()?.activeCategoryId ?? null;
  }

  updateActiveCategory(activeCategoryId: string): void {
    const state = this.load();
    if (!state) {
      return;
    }
    this.save({ ...state, activeCategoryId });
  }

  clearIfProductTypeMismatch(allowedProductTypes: readonly ProductType[]): void {
    const state = this.load();
    if (state?.productType && !allowedProductTypes.includes(state.productType)) {
      this.clear();
    }
  }

  isAuthenticated(): boolean {
    const token = this.token;
    if (!token) {
      return false;
    }
    const exp = parseJwtExp(token);
    if (!exp) {
      // Tokens without an exp claim (e.g. long-lived service tokens or SSO-issued tokens
      // that omit expiry) are treated as valid. Actual session expiry for these is enforced
      // server-side via 401 responses handled by ErrorInterceptor.
      return true;
    }
    return Date.now() < exp * 1000;
  }
}
